import { apiClient, type ApiResponse } from './client';

export interface IssueQueueItem {
    id: string;
    prd_id: string;
    work_order_id?: string | null;
    bom_component_id?: string | null;
    item_id: string;
    item_name: string;
    item_code: string;
    unit: string;
    required_qty: number;
    issued_qty: number;
    /** PARTIAL: part issued, the rest pending */
    status: 'PENDING' | 'PARTIAL' | 'ISSUED' | 'CANCELLED';
    stage_number: number;
    stage_name: string;
    warehouse_id?: string | null;
    warehouse_name?: string | null;
    po_number?: string;
    product_name?: string;
    po_planned_qty?: number;
    wo_number?: string | null;
    is_locked?: boolean;
    stock_available?: number;
    issued_at?: string | null;
    issued_by?: string | null;
    issued_by_name?: string | null;
    notes?: string | null;
    created_at: string;
    /** Batch-tracked material: issued from picked batches or FIFO */
    is_batch_tracked?: boolean;
    /** Warehouse the line is issued from (its own, else the order's) */
    issue_warehouse_id?: string | null;
    /** Still to issue (required − issued) */
    pending_qty?: number;
    /** What the order can take now: on hand less other orders' reservations */
    free_qty?: number;
    /** Batches an issued line came from */
    batch_allocations?: { batchId: string; batchNumber: string; qty: number }[] | null;
}

/** A batch and how much of it to issue; no picks = FIFO */
export interface IssueBatchPick {
    batchId: string;
    qty: number;
}

export interface IssueQueueSummary {
    total_items: number;
    pending_count: number;
    partial_count?: number;
    issued_count: number;
    shortage_count: number;
}

export interface IssueQueueResponse {
    success: boolean;
    summary?: IssueQueueSummary;
    count: number;
    data: IssueQueueItem[];
}

const BASE = '/api/v1/manufacturing/production-orders';

export async function getAllIssueQueue(params?: {
    status?: string;
    warehouse_id?: string;
    search?: string;
}): Promise<IssueQueueResponse> {
    const { data } = await apiClient.get<IssueQueueResponse>(`${BASE}/issue-queue`, {
        params,
    });
    return data;
}

export async function getProductionOrderIssueQueue(
    prdId: string,
    status?: string
): Promise<IssueQueueResponse> {
    const { data } = await apiClient.get<IssueQueueResponse>(`${BASE}/${prdId}/issue-queue`, {
        params: status ? { status } : undefined,
    });
    return data;
}

/** Issues `qty` (default: what is pending; less = partial issue) from the picked batches or FIFO */
export async function confirmIssueQueueItem(
    prdId: string,
    queueId: string,
    batches?: IssueBatchPick[],
    qty?: number
): Promise<ApiResponse<IssueQueueItem>> {
    const { data } = await apiClient.post<ApiResponse<IssueQueueItem>>(
        `${BASE}/${prdId}/issue-queue/${queueId}/confirm`,
        { ...(batches?.length ? { batches } : {}), ...(qty !== undefined ? { qty } : {}) }
    );
    return data;
}

/**
 * `batches` = picked batches per queue line (others FIFO); `quantities` = what to issue per line
 * (partial issue; 0 leaves a line pending). Without quantities every line is issued in full.
 */
export async function confirmStageIssueQueue(
    prdId: string,
    stageNumber: number,
    batches?: Record<string, IssueBatchPick[]>,
    quantities?: Record<string, number>
): Promise<ApiResponse<IssueQueueItem[]>> {
    const { data } = await apiClient.post<ApiResponse<IssueQueueItem[]>>(
        `${BASE}/${prdId}/issue-queue/stage/${stageNumber}/confirm`,
        {
            ...(batches && Object.keys(batches).length ? { batches } : {}),
            ...(quantities ? { quantities } : {}),
        }
    );
    return data;
}
